import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { toast } from 'sonner';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  CalendarCheck,
  MapPin,
  Briefcase,
  Search,
  PoundSterling,
  Shield,
  Bus,
  Hotel,
  Tent,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Users,
  Shirt,
  ArrowUpDown,
  MessageCircle,
  Hand,
  Building2,
  Clock as ClockIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact banner — informational only. Employees no longer claim directly;
// they file a request for admin approval. This banner tells them how to
// reach admin if they need details.
const ADMIN_WHATSAPP_NUMBER = '+44 7365 928174';
const ADMIN_WHATSAPP_HREF = 'https://wa.me/447365928174';
const CONTACT_MESSAGE = `Have questions or need event details? Contact Admin on WhatsApp ${ADMIN_WHATSAPP_NUMBER}.`;

// Events using this category are treated as "package" — one claim covers
// every day in the group. Anything else lets the employee pick per-day.
// Note: backend stores the value in `shift_category` (create form) but some
// legacy rows use `category`, so we check both for backwards compatibility.
const PACKAGE_CATEGORIES = new Set(['events', 'event', 'festival', 'show']);

function isPackageEvent(group) {
  const raw = group?.shift_category ?? group?.category ?? '';
  const cat = String(raw).toLowerCase().trim();
  return PACKAGE_CATEGORIES.has(cat);
}

// Group shifts by (location, position) — those two together represent a
// single event for the employee's perspective. Times are intentionally
// ignored from the grouping key because the spec says "working hours will
// be confirmed by management" and many events have placeholder times.
function groupShifts(shifts) {
  const groups = {};
  shifts.forEach((s) => {
    const key = `${s.location || ''}||${s.position || ''}`;
    if (!groups[key]) {
      groups[key] = {
        key,
        location: s.location || 'TBD',
        position: s.position || 'TBD',
        dress_code: s.dress_code || '',
        hourly_payment: s.hourly_payment,
        sia_required: s.sia_required,
        notes: s.notes,
        transport_notes: s.transport_notes || '',
        accommodation_notes: s.accommodation_notes || '',
        transport_location: s.transport_location || '',
        transport_provided: s.transport_provided,
        transport_departure_time: s.transport_departure_time || '',
        camping_provided: s.camping_provided,
        accommodation_provided: s.accommodation_provided,
        accommodation_cost_per_day: s.accommodation_cost_per_day,
        // Backend stores category under either `shift_category` (new) or
        // `category` (legacy). Propagate so isPackageEvent() can decide.
        shift_category: s.shift_category || s.category || '',
        category: s.category || s.shift_category || '',
        company_name: s.company_name || '',
        address: s.address || '',
        postcode: s.postcode || '',
        source: s.source || '',
        shifts: [],
      };
    }
    groups[key].shifts.push(s);
  });
  Object.values(groups).forEach((g) =>
    g.shifts.sort((a, b) => (a.date || '').localeCompare(b.date || '')),
  );
  return Object.values(groups);
}

// Only days that still have space AND that I haven't already claimed are
// sent in a claim — full days stay with the event but can't be requested.
function claimableDayIds(group, userId) {
  return group.shifts
    .filter((s) =>
      (s.claimed_count || 0) < (s.quantity || 1) &&
      !(s.claimed_by_list || []).some((c) => c.user_id === userId))
    .map((s) => s.id);
}

// "Days available" = days where current user has NOT yet claimed and the
// day still has at least one open slot. "Days claimed by me" = days where
// the user already appears in claimed_by_list.
function dayStats(group, userId) {
  let claimable = 0;
  let claimedByMe = 0;
  group.shifts.forEach((s) => {
    const isMine = !!(s.claimed_by_list || []).find((c) => c.user_id === userId);
    if (isMine) {
      claimedByMe += 1;
    } else if ((s.claimed_count || 0) < (s.quantity || 1)) {
      claimable += 1;
    }
  });
  return { claimable, claimedByMe, total: group.shifts.length };
}

// Group-level spaces-left = max remaining slot capacity across any of the
// days. This matches how the rest of the app reasons about availability.
function spacesLeft(group) {
  if (!group.shifts.length) return 0;
  // Days can have different capacities — report the best (max) number of
  // open spaces across the days that still have room. FULL only when every
  // single day is full.
  return Math.max(0, ...group.shifts.map((s) => (s.quantity || 1) - (s.claimed_count || 0)));
}

function pickAvailability(group) {
  const left = spacesLeft(group);
  if (left === 0) return { kind: 'full', label: 'FULL', cls: 'bg-red-500/15 text-red-400 border-red-500/40' };
  if (left <= 5) return { kind: 'low', label: `${left} space${left === 1 ? '' : 's'} left`, cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' };
  return { kind: 'open', label: `${left} spaces left`, cls: 'bg-green-500/15 text-green-400 border-green-500/40' };
}

// ---- Quick filter chips ----
// 'sia/steward/cleaner' match against position with a forgiving substring
// check so variants like "SIA Door Supervisor" or "Steward Lead" still
// land in the right bucket. 'with_camping' / 'with_accommodation' filter
// against the boolean fields.
function matchesFilter(group, filter) {
  if (filter === 'all') return true;
  const pos = (group.position || '').toLowerCase();
  if (filter === 'sia') return pos.includes('sia');
  if (filter === 'steward') return pos.includes('steward');
  if (filter === 'cleaner') return pos.includes('cleaner');
  if (filter === 'with_camping') return group.camping_provided === true;
  if (filter === 'with_accommodation') return group.accommodation_provided === true;
  return true;
}

function sortGroups(groups, sort) {
  const copy = [...groups];
  if (sort === 'highest_pay') {
    return copy.sort((a, b) => (Number(b.hourly_payment) || 0) - (Number(a.hourly_payment) || 0));
  }
  if (sort === 'most_spaces') {
    return copy.sort((a, b) => spacesLeft(b) - spacesLeft(a));
  }
  // 'earliest' and 'closest_event' both effectively sort by first date.
  // We keep them as two options so users can pick whichever they prefer.
  return copy.sort((a, b) => {
    const ad = a.shifts[0]?.date || '';
    const bd = b.shifts[0]?.date || '';
    return ad.localeCompare(bd);
  });
}

export default function EmployeeAvailableShiftsPage() {
  const { user } = useAuth();
  const Layout = user?.role === 'supervisor' ? SupervisorLayout : EmployeeLayout;
  const userId = user?.id;
  const [availableShifts, setAvailableShifts] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('earliest');
  const [moreOpen, setMoreOpen] = useState({});
  const [claimDialog, setClaimDialog] = useState({ open: false, group: null, dayIds: null });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchAvailableShifts = useCallback(async () => {
    try {
      const [openRes, myReqRes] = await Promise.all([
        axios.get(`${API}/open-shifts`),
        axios.get(`${API}/me/claim-requests`).catch(() => ({ data: [] })),
      ]);
      setAvailableShifts(openRes.data || []);
      setMyRequests(myReqRes.data || []);
    } catch (error) {
      console.error('Failed to fetch available shifts:', error);
      toast.error('Failed to load available shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAvailableShifts(); }, [fetchAvailableShifts]);
  useAutoRefresh(fetchAvailableShifts);

  // A pending request is one submitted for at least one shift in this group.
  const pendingRequestForGroup = useCallback((group) => {
    const gIds = new Set(group.shifts.map((s) => s.id));
    return (myRequests || []).find(
      (r) => r.status === 'pending' && (r.shift_ids || []).some((id) => gIds.has(id))
    );
  }, [myRequests]);

  const submitClaim = async (shiftIds) => {
    if (!shiftIds || !shiftIds.length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/open-shifts/${shiftIds[0]}/claim-request`, {
        shift_ids: shiftIds,
      });
      toast.success('Request submitted — awaiting admin approval');
      setClaimDialog({ open: false, group: null, dayIds: null });
      await fetchAvailableShifts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const groups = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    // (1) Hide fully-staffed shifts before grouping so events that
    //     have every day filled disappear from the employee list.
    // (2) Hide any shift where I'm already assigned — clock in from
    //     the My Shifts page instead.
    const filtered = availableShifts.filter((s) => {
      const full = (s.claimed_count || 0) >= (s.quantity || 1);
      if (full) return false;
      const mine = (s.claimed_by_list || []).some((c) => c.user_id === userId);
      if (mine) return false;
      const matchesSearch =
        (s.location || '').toLowerCase().includes(q) ||
        (s.position || '').toLowerCase().includes(q);
      return matchesSearch;
    });
    const grouped = groupShifts(filtered);
    // (3) Drop empty groups that ended up with zero visible days.
    const withDays = grouped.filter((g) => g.shifts && g.shifts.length > 0);
    return sortGroups(withDays.filter((g) => matchesFilter(g, filter)), sort);
  }, [availableShifts, searchTerm, filter, sort, userId]);

  // Detail view: triggered via URL hash so back button works naturally.
  const selectedKey = location.hash && location.hash.startsWith('#event=')
    ? decodeURIComponent(location.hash.slice('#event='.length))
    : null;
  const selectedGroup = useMemo(
    () => (selectedKey ? groupShifts(availableShifts).find((g) => g.key === selectedKey) : null),
    [selectedKey, availableShifts],
  );

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Layout>
    );
  }

  // Detail view replaces the list (single-page navigation, keeps it fast)
  const basePath = user?.role === 'supervisor' ? '/supervisor/available-shifts' : '/employee/available-shifts';
  if (selectedGroup) {
    return (
      <Layout>
        <EventDetail
          group={selectedGroup}
          pendingReq={pendingRequestForGroup(selectedGroup)}
          onBack={() => navigate(basePath)}
          onClaimAll={(g) => setClaimDialog({ open: true, group: g, dayIds: claimableDayIds(g, userId) })}
          onClaimDay={(g, dayId) => setClaimDialog({ open: true, group: g, dayIds: [dayId] })}
        />
        <ClaimConfirmDialog
          dialog={claimDialog}
          submitting={submitting}
          onCancel={() => setClaimDialog({ open: false, group: null, dayIds: null })}
          onConfirm={() => submitClaim(claimDialog.dayIds)}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-4 min-h-screen max-w-3xl mx-auto">
        <div className="border-b border-border pb-4">
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <CalendarCheck className="w-6 h-6 text-primary" strokeWidth={1.5} />
            AVAILABLE SHIFTS
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Browse upcoming shifts and request the ones you&apos;d like to work.
            Admin approval is required before you&apos;re confirmed.
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by location or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-background border-border rounded-sm"
            data-testid="search-available-shifts"
          />
        </div>

        {/* Quick filters */}
        <div className="flex flex-wrap gap-1.5" data-testid="filter-chips">
          {[
            { key: 'all', label: 'All' },
            { key: 'sia', label: 'SIA' },
            { key: 'steward', label: 'Steward' },
            { key: 'cleaner', label: 'Cleaner' },
            { key: 'with_camping', label: 'With Camping' },
            { key: 'with_accommodation', label: 'With Accommodation' },
          ].map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? 'default' : 'outline'}
              onClick={() => setFilter(f.key)}
              className="rounded-sm text-xs h-8 px-3"
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="bg-background border-border rounded-sm h-9 text-xs w-full sm:w-64" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="earliest">Earliest first</SelectItem>
              <SelectItem value="highest_pay">Highest pay</SelectItem>
              <SelectItem value="most_spaces">Most spaces available</SelectItem>
              <SelectItem value="closest_event">Closest event</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {groups.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center space-y-2">
              <CalendarCheck className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">No matching shifts available right now.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {groups.map((group) => (
              <CompactCard
                key={group.key}
                group={group}
                userId={userId}
                pendingReq={pendingRequestForGroup(group)}
                moreOpen={!!moreOpen[group.key]}
                onToggleMore={() => setMoreOpen((p) => ({ ...p, [group.key]: !p[group.key] }))}
                onOpenDetail={() => navigate(`/employee/available-shifts#event=${encodeURIComponent(group.key)}`)}
                onClaimAll={(g) => setClaimDialog({ open: true, group: g, dayIds: claimableDayIds(g, userId) })}
              />
            ))}
          </div>
        )}
      </div>
      <ClaimConfirmDialog
        dialog={claimDialog}
        submitting={submitting}
        onCancel={() => setClaimDialog({ open: false, group: null, dayIds: null })}
        onConfirm={() => submitClaim(claimDialog.dayIds)}
      />
    </Layout>
  );
}

// ----- Compact card -----
function CompactCard({ group, userId, pendingReq, moreOpen, onToggleMore, onOpenDetail, onClaimAll }) {
  const stats = dayStats(group, userId);
  const avail = pickAvailability(group);
  const isPackage = isPackageEvent(group);
  const dateRange = (() => {
    const dates = [...new Set(group.shifts.map((s) => s.date))].sort();
    if (!dates.length) return '';
    try {
      if (dates.length === 1) return format(parseISO(dates[0]), 'EEE dd MMM yyyy');
      return `${format(parseISO(dates[0]), 'dd MMM')} → ${format(parseISO(dates[dates.length - 1]), 'dd MMM yyyy')}`;
    } catch { return `${dates[0]} → ${dates[dates.length - 1]}`; }
  })();
  const hasMore = !!(group.transport_provided !== null || group.camping_provided !== null || group.accommodation_provided !== null || group.transport_notes || group.accommodation_notes);
  const dayCount = group.shifts.length;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid={`event-group-${group.key}`}>
      <CardContent className="p-4 space-y-3">
        {/* Header — most prominent: position + availability badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-lg font-semibold leading-tight flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{group.position}</span>
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{group.location}</span>
            </p>
            {group.address && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate pl-5" data-testid={`address-${group.key}`}>
                {group.address}{group.postcode ? `, ${group.postcode}` : ''}
              </p>
            )}
            {group.source === 'client_request' && group.company_name && (
              <p className="text-xs text-primary mt-0.5 flex items-center gap-1" data-testid={`company-${group.key}`}>
                <Building2 className="w-3 h-3 shrink-0" />
                <span className="truncate">{group.company_name}</span>
              </p>
            )}
          </div>
          <Badge className={`text-[10px] uppercase tracking-wider border ${avail.cls} shrink-0`} data-testid={`avail-${group.key}`}>
            <Users className="w-3 h-3 mr-1" />
            {avail.label}
          </Badge>
        </div>

        {/* Dates row */}
        <p className="text-sm text-primary font-medium">{dateRange}</p>

        {/* Hourly pay highlight + days stats */}
        <div className="grid grid-cols-2 gap-2">
          {group.hourly_payment ? (
            <div className="bg-green-500/5 border border-green-500/30 rounded-sm px-2 py-1.5 flex items-center gap-1.5">
              <PoundSterling className="w-3.5 h-3.5 text-green-400 shrink-0" />
              <span className="text-sm font-semibold text-green-300">
                £{Number(group.hourly_payment).toFixed(2)} / hr
              </span>
            </div>
          ) : <div />}
          <div className="bg-muted/30 border border-border/60 rounded-sm px-2 py-1.5 text-xs">
            <p className="text-foreground">{stats.total} day{stats.total !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {group.sia_required && (
          <Badge variant="outline" className="text-[10px] uppercase border-amber-500/30 text-amber-300 bg-amber-500/5">
            <Shield className="w-3 h-3 mr-1" />
            SIA: {group.sia_required === 'door_supervisor' ? 'Door Supervisor' : group.sia_required}
          </Badge>
        )}

        <p className="text-[11px] text-muted-foreground italic">
          Working hours will be confirmed by management.
        </p>

        {/* Collapsible "More Information" */}
        {hasMore && (
          <div>
            <button
              type="button"
              onClick={onToggleMore}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              aria-expanded={moreOpen}
              data-testid={`more-toggle-${group.key}`}
            >
              {moreOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              More Information
            </button>
            {moreOpen && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <ProvidedBadge label="Camping" provided={group.camping_provided} icon={<Tent className="w-3 h-3" />} />
                <ProvidedBadge label="Accommodation" provided={group.accommodation_provided} icon={<Hotel className="w-3 h-3" />} />
                <ProvidedBadge label="Transport" provided={group.transport_provided} icon={<Bus className="w-3 h-3" />} />
                {group.dress_code && (
                  <Badge variant="outline" className="text-[10px] uppercase border-blue-500/30 text-blue-300 bg-blue-500/5">
                    <Shirt className="w-3 h-3 mr-1" />
                    {group.dress_code}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        {/* Claim action / pending state — package events get a single
            "Claim event" button; per-day events surface a "See days"
            hint pointing to the detail view. */}
        {pendingReq ? (
          <div className="rounded-sm bg-yellow-500/5 border border-yellow-500/30 px-3 py-2">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-yellow-300 shrink-0" />
              <span className="text-xs text-yellow-100 font-medium">Awaiting admin approval</span>
            </div>
          </div>
        ) : isPackage ? (
          <Button
            size="sm"
            className="w-full rounded-sm text-xs uppercase bg-primary text-primary-foreground hover:bg-yellow-300 font-semibold"
            onClick={() => onClaimAll(group)}
            data-testid={`claim-event-${group.key}`}
          >
            <Hand className="w-3.5 h-3.5 mr-1" />
            Claim Event – {dayCount} day{dayCount !== 1 ? 's' : ''}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full rounded-sm text-xs uppercase border-primary/40 text-primary hover:bg-primary/10"
            onClick={onOpenDetail}
            data-testid={`pick-days-${group.key}`}
          >
            Pick individual days
          </Button>
        )}

        {/* Contact banner — informational, kept below the primary action */}
        <ContactAdminBanner data-testid={`contact-admin-${group.key}`} />

        <div className="pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenDetail}
            className="w-full rounded-sm text-xs uppercase text-muted-foreground hover:text-primary"
            data-testid={`view-details-${group.key}`}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ContactAdminBanner({ 'data-testid': dataTestId }) {
  return (
    <a
      href={ADMIN_WHATSAPP_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-emerald-500/5 border border-emerald-500/30 rounded-sm px-3 py-2 hover:bg-emerald-500/10 transition-colors"
      data-testid={dataTestId || 'contact-admin-banner'}
    >
      <div className="flex items-start gap-2">
        <MessageCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-100 leading-relaxed">
          {CONTACT_MESSAGE}
        </p>
      </div>
    </a>
  );
}

function ProvidedBadge({ label, provided, icon }) {
  if (provided === undefined || provided === null) return null;
  const cls = provided
    ? 'bg-green-500/10 text-green-400 border-green-500/30'
    : 'bg-red-500/10 text-red-400 border-red-500/30';
  return (
    <Badge variant="outline" className={`text-[10px] uppercase border ${cls} flex items-center gap-1`}>
      {icon}
      {label}: {provided ? 'Provided' : 'Not Provided'}
    </Badge>
  );
}

// ----- Detail page (per-day list, view-only + per-day claim) -----
function EventDetail({ group, pendingReq, onBack, onClaimAll, onClaimDay }) {
  const avail = pickAvailability(group);
  const isPackage = isPackageEvent(group);
  const pendingIds = new Set((pendingReq?.shift_ids) || []);
  const openDayCount = group.shifts.filter((s) => (s.claimed_count || 0) < (s.quantity || 1)).length;

  return (
    <div className="p-4 sm:p-6 space-y-4 min-h-screen max-w-3xl mx-auto">
      <Button variant="outline" size="sm" onClick={onBack} className="border-border rounded-sm" data-testid="event-detail-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" />
        Back to Available Shifts
      </Button>

      <div className="space-y-2">
        <h1 className="text-2xl font-barlow tracking-widest text-foreground">{group.position}</h1>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-4 h-4" />
          {group.location}
        </p>
        {group.address && (
          <p className="text-xs text-muted-foreground pl-6" data-testid="event-detail-address">
            {group.address}{group.postcode ? `, ${group.postcode}` : ''}
          </p>
        )}
        {group.source === 'client_request' && group.company_name && (
          <p className="text-sm text-primary flex items-center gap-1.5" data-testid="event-detail-company">
            <Building2 className="w-4 h-4" />
            {group.company_name}
          </p>
        )}
      </div>

      {/* Highlight strip — pay + availability */}
      <div className="grid grid-cols-2 gap-2">
        {group.hourly_payment && (
          <div className="bg-green-500/5 border border-green-500/30 rounded-sm px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Hourly pay</p>
            <p className="text-base font-semibold text-green-300">£{Number(group.hourly_payment).toFixed(2)} / hr</p>
          </div>
        )}
        <div className="rounded-sm border px-3 py-2 bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Availability</p>
          <Badge className={`text-[10px] uppercase tracking-wider border ${avail.cls} mt-1`}>
            <Users className="w-3 h-3 mr-1" />
            {avail.label}
          </Badge>
        </div>
      </div>

      <div className="text-sm text-muted-foreground italic">
        Working hours will be confirmed by management.
      </div>

      {/* Amenity badges */}
      <div className="flex flex-wrap gap-1.5">
        {group.sia_required && (
          <Badge variant="outline" className="text-[10px] uppercase border-amber-500/30 text-amber-300 bg-amber-500/5">
            <Shield className="w-3 h-3 mr-1" />
            SIA: {group.sia_required === 'door_supervisor' ? 'Door Supervisor' : group.sia_required}
          </Badge>
        )}
        <ProvidedBadge label="Camping" provided={group.camping_provided} icon={<Tent className="w-3 h-3" />} />
        <ProvidedBadge label="Accommodation" provided={group.accommodation_provided} icon={<Hotel className="w-3 h-3" />} />
        <ProvidedBadge label="Transport" provided={group.transport_provided} icon={<Bus className="w-3 h-3" />} />
        {group.dress_code && (
          <Badge variant="outline" className="text-[10px] uppercase border-blue-500/30 text-blue-300 bg-blue-500/5">
            <Shirt className="w-3 h-3 mr-1" />
            {group.dress_code}
          </Badge>
        )}
      </div>

      {(group.notes || group.transport_notes || group.accommodation_notes) && (
        <div className="space-y-1 border-l-2 border-border/60 pl-3">
          {group.notes && <p className="text-xs text-muted-foreground">{group.notes}</p>}
          {group.transport_notes && <p className="text-xs text-muted-foreground"><strong>Transport:</strong> {group.transport_notes}</p>}
          {group.accommodation_notes && <p className="text-xs text-muted-foreground"><strong>Accommodation:</strong> {group.accommodation_notes}</p>}
        </div>
      )}

      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Days</p>
      <div className="space-y-2">
        {group.shifts.filter((s) => {
          // Full days are hidden from the employee entirely — unless the day
          // is part of THIS user's pending claim request (keep it visible
          // with its Pending badge so they know what they asked for).
          const isFull = (s.claimed_count || 0) >= (s.quantity || 1);
          return !isFull || pendingIds.has(s.id);
        }).map((s) => {
          const isFull = (s.claimed_count || 0) >= (s.quantity || 1);
          const dayPending = pendingIds.has(s.id);
          let dateLabel = s.date;
          try { dateLabel = format(parseISO(s.date), 'EEE dd MMM yyyy'); } catch { /* keep raw */ }
          return (
            <div
              key={s.id}
              className="flex items-center justify-between gap-2 p-3 bg-card border border-border rounded-sm"
              data-testid={`day-row-${s.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{dateLabel}</p>
                <p className="text-xs text-muted-foreground">
                  {(s.claimed_count || 0)}/{s.quantity || 1} filled
                </p>
              </div>
              {dayPending ? (
                <Badge className="bg-yellow-500/10 text-yellow-300 border-yellow-500/30 text-[10px] uppercase">
                  Pending
                </Badge>
              ) : isFull ? (
                <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] uppercase">Full</Badge>
              ) : isPackage ? (
                <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] uppercase">Available</Badge>
              ) : (
                <Button
                  size="sm"
                  onClick={() => onClaimDay(group, s.id)}
                  className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-xs"
                  data-testid={`claim-day-${s.id}`}
                >
                  <Hand className="w-3.5 h-3.5 mr-1" />
                  Claim
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Package events: single button to claim the whole thing */}
      {isPackage && !pendingReq && openDayCount > 0 && (
        <Button
          className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider font-semibold"
          onClick={() => onClaimAll(group)}
          data-testid="claim-event-detail-btn"
        >
          <Hand className="w-4 h-4 mr-1.5" />
          Claim Event – {openDayCount} day{openDayCount !== 1 ? 's' : ''}
        </Button>
      )}
      {pendingReq && (
        <div className="rounded-sm bg-yellow-500/5 border border-yellow-500/30 px-3 py-3">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-4 h-4 text-yellow-300" />
            <span className="text-sm text-yellow-100 font-medium">Awaiting admin approval</span>
          </div>
        </div>
      )}

      <ContactAdminBanner data-testid="contact-admin-detail" />
    </div>
  );
}

// ----- Claim confirmation dialog -----
function ClaimConfirmDialog({ dialog, submitting, onCancel, onConfirm }) {
  const g = dialog.group;
  const nDays = (dialog.dayIds || []).length;
  return (
    <AlertDialog open={dialog.open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent className="bg-card border-border rounded-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-barlow tracking-wider uppercase">
            Send claim request?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {g
              ? `You're about to request ${nDays} day${nDays === 1 ? '' : 's'} for ${g.position} at ${g.location}. Admin will review and approve or decline.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm" disabled={submitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={submitting}
            className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
            data-testid="confirm-claim-request"
          >
            {submitting ? 'Submitting...' : 'Send request'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
